import React, { FC } from 'react';
import { parseTime, sortByDate } from '../utils/dateTime';
import { AppointmentInfo } from '../types';
import GenericCarousel from './GenericCarousel';
import styles from './AppointmentList.module.css';
import GenericCard, { GenericContentType } from './GenericCard';
import { ReactComponent as DirectionsPinSvg } from '../icons/directions_pin.svg';
import { ReactComponent as TimeClockSvg } from '../icons/time_clock.svg';
import { format } from '../helpers';

import { AmpFlagSet } from '../amplitude';
import generalConfig from '../resources/general.json';

export type AppointmentsAttachmentData = {
    appointments: AppointmentInfo[];
    url: string;
};

const getDateOfAppointment = (appointmentinfo: AppointmentInfo): Date => {
    const date = new Date(appointmentinfo.date);
    const timeParts = parseTime(appointmentinfo.appointmentStartTime);
    date.setHours(timeParts[0], timeParts[1]);
    return date;
};

const Appointment: FC<{
    appointmentInfo: AppointmentInfo;
    featureFlags: AmpFlagSet;
    url: string;
}> = ({ appointmentInfo, featureFlags, url }) => {
    const datetime = getDateOfAppointment(appointmentInfo);
    const weekday = datetime.toLocaleDateString('en-us', { weekday: 'long' });
    const month = datetime.toLocaleDateString('en-us', { month: 'long' });
    const date = datetime.getDate();
    const year = datetime.getFullYear();
    const addressObj = appointmentInfo?.providers[0]?.address;
    let address = '';
    if (addressObj) {
        address = `${addressObj.line1}, `;
        if (addressObj.line2) {
            address += `${addressObj.line2}, `;
        }
        address += `${addressObj.city}, ${addressObj.state} ${addressObj.postalCode}`;
    }
    return (
        <GenericCard
            data={{
                title: { text: appointmentInfo.providers[0].providerName },
                subtitles: [{ text: appointmentInfo.visitTypeName }],
                content: [
                    {
                        type: GenericContentType.Info,
                        title: { text: `${weekday} ${month} ${date}, ${year}` },
                        info: {
                            text: format(
                                generalConfig.providence
                                    .appointmentListStartsAt,
                                {
                                    startTime:
                                        appointmentInfo?.appointmentStartTime,
                                }
                            ),
                        },
                        image: (
                            <TimeClockSvg
                                className={styles.appointmentListContentIcon}
                            />
                        ),
                    },
                    {
                        type: GenericContentType.Info,
                        title: {
                            text: appointmentInfo?.providers[0]?.departmentName,
                        },
                        info: { text: address },
                        image: (
                            <DirectionsPinSvg
                                className={styles.appointmentListContentIcon}
                            />
                        ),
                    },
                ],
                buttonData: {
                    title: generalConfig.providence
                        .appointmentListSeeMyAppointments,
                    url,
                },
            }}
            featureFlags={featureFlags}
        />
    );
};

export const AppointmentList: FC<{
    data: AppointmentsAttachmentData;
    featureFlags: AmpFlagSet;
}> = ({ data, featureFlags }) => {
    const sortedAppointments = data.appointments.sort((appAInfo, appBInfo) => {
        const dateA = new Date(getDateOfAppointment(appAInfo));
        const dateB = new Date(getDateOfAppointment(appBInfo));
        return sortByDate(dateA, dateB);
    });

    const appointmentComponents = sortedAppointments.map((appointment) => (
        <Appointment
            appointmentInfo={appointment as AppointmentInfo}
            featureFlags={featureFlags}
            url={data.url}
        />
    ));

    return <GenericCarousel elements={appointmentComponents} />;
};
